import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

export const Layout = styled(motion.div)`
  padding: 0px;
  margin: 0px;
  background: #0a0a0b;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  align-content: center;
  box-sizing: border-box;
  position: fixed;
  overflow: auto;
  -webkit-tap-highlight-color: transparent;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 2px;
    height: 1px;
  }
  &::-webkit-scrollbar-track {
    background: #1e1e1e;
  }
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(
      131.42deg,
      #e7557055 32.19%,
      #ed676a55 53.45%,
      #f1726755 66.7%,
      #fd965b55 83.78%
    );
    border-radius: 8px;
    opacity: 0.1;
  }
`;
export interface StackProps {
  direction?: string | undefined;
  align?: 'start' | 'end' | 'center' | 'between' | 'evenly' | undefined;
  width?: string | undefined;
  height?: string | undefined;
  wrap?: string | undefined;
  margin?: string;
  padding?: string;
  position?:
    | 'fixed'
    | 'relative'
    | 'sticky'
    | 'absolute'
    | 'static'
    | '-webkit-sticky'
    | 'inherit'
    | 'initial'
    | '-ms-page'
    | 'unset'
    | undefined;
  display?: string;
  disableSelectText?: boolean;
  disableDrag?: boolean;
  isPressble?: boolean;
  zIndex?: number;
  opacity?: number;
}

export const AppContainer = styled.div<StackProps>`
  position: ${({ position }) => position};
  display: ${({ display }) => display ?? 'flex'};
  margin: ${({ margin }) => margin};
  padding: ${({ padding }) => padding};
  flex-wrap: ${({ wrap }) => wrap};
  width: ${({ width }) => (width ? width : '100%')};
  height: ${({ height }) => (height ? height : 'auto')};
  flex-direction: ${({ direction }) => direction};
  align-items: ${p => {
    switch (p.align) {
      case 'center':
        return 'center';
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'between':
        return 'space-between';
      case 'evenly':
        return 'space-evenly';
      default:
        return undefined;
    }
  }};
  ${p =>
    p.disableSelectText === true &&
    css`
      user-select: none;
      -ms-user-select: none;
      -moz-user-select: none;
      -ms-touch-select: none;
      -webkit-user-select: none;
    `}
  ${p =>
    p.disableDrag === true &&
    css`
      user-drag: none;
      -moz-window-dragging: none;
      -webkit-user-drag: none;
    `}
  justify-content: ${p => {
    switch (p.align) {
      case 'center':
        return 'center';
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'between':
        return 'space-between';
      case 'evenly':
        return 'space-evenly';
      default:
        return undefined;
    }
  }};

  @media only screen and (min-width: 500px) {
    max-width: 500px;
  }
`;

export const Stack = styled.div<StackProps>`
  position: ${({ position }) => position};
  display: ${({ display }) => display ?? 'flex'};
  margin: ${({ margin }) => margin};
  padding: ${({ padding }) => padding};
  opacity: ${({ opacity }) => opacity};
  flex-wrap: ${({ wrap }) => wrap};
  width: ${({ width }) => (width ? width : '100%')};
  height: ${({ height }) => (height ? height : 'auto')};
  flex-direction: ${({ direction }) => direction};
  z-index: ${({ zIndex }) => zIndex};
  align-items: ${p => {
    switch (p.align) {
      case 'center':
        return 'center';
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'between':
        return 'space-between';
      case 'evenly':
        return 'space-evenly';
      default:
        return undefined;
    }
  }};
  justify-content: ${p => {
    switch (p.align) {
      case 'center':
        return 'center';
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'between':
        return 'space-between';
      case 'evenly':
        return 'space-evenly';
      default:
        return undefined;
    }
  }};
  ${p =>
    p.disableSelectText === true &&
    css`
      user-select: none;
      -ms-user-select: none;
      -moz-user-select: none;
      -ms-touch-select: none;
      -webkit-user-select: none;
    `}
  ${p =>
    p.disableDrag === true &&
    css`
      user-drag: none;
      -moz-window-dragging: none;
      -webkit-user-drag: none;
    `}
  ${({ isPressble }) =>
    isPressble === true &&
    css`
      user-select: none;
      -ms-user-select: none;
      -moz-user-select: none;
      -ms-touch-select: none;
      -webkit-user-select: none;
      cursor: pointer;
      &:active {
        opacity: 0.9;
      }
    `}
`;

export interface BoxProps {
  width?: string | undefined;
  height?: string | undefined;
  minHeight?: string | undefined;
  maxHeight?: string;
  minWidth?: string;
  maxWidth?: string;
  margin?: string | undefined;
  padding?: string | undefined;
  border?: string | undefined;
  borderRadius?: string | undefined;
  background?: string | undefined;
  position?: string | undefined;
  flexGrow?: number | undefined;
  flexShrink?: number | undefined;
  flexWrap?: string | undefined;
  shadow?: string | undefined;
  order?: number | undefined;
  flexBasis?: string | undefined;
  alignSelf?: 'start' | 'center' | 'end' | undefined;
  isPressble?: boolean;
  disableDrag?: boolean;
  opacity?: number;
  display?: 'flex' | 'block' | 'inline-block' | string;
}

export const Box = styled.div<BoxProps>`
  font-family: 'Roboto', sans-serif;
  display: ${({ display }) => display ?? 'flex'};
  width: ${({ width }) => width};
  min-width: ${({ minWidth }) => minWidth};
  max-width: ${({ maxWidth }) => maxWidth};
  height: ${({ height }) => height};
  max-height: ${({ maxHeight }) => maxHeight};
  flex-wrap: ${({ flexWrap }) => flexWrap};
  min-height: ${({ minHeight }) => minHeight};
  margin: ${({ margin }) => margin};
  padding: ${({ padding }) => padding};
  box-shadow: ${({ shadow }) => shadow};
  border: ${({ border }) => border ?? 'none'};
  border-radius: ${({ borderRadius }) => borderRadius};
  background: ${({ background }) => background};
  position: ${({ position }) => position};
  flex-grow: ${({ flexGrow }) => flexGrow};
  flex-shrink: ${({ flexShrink }) => flexShrink};
  flex-basis: ${({ flexBasis }) => flexBasis};
  opacity: ${({ opacity }) => opacity};
  -webkit-opacity: ${({ opacity }) => opacity};
  -moz-opacity: ${({ opacity }) => opacity};
  -khtml-opacity: ${({ opacity }) => opacity};
  filter: ${({ opacity }) => (opacity ? `alpha(opacity=${opacity * 100})` : '')};
  -ms-filter: ${({ opacity }) =>
    opacity ? `progid:DXImageTransform.Microsoft.Alpha(Opacity=${opacity * 100})` : ''};
  align-self: ${({ alignSelf }) => {
    switch (alignSelf) {
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'center':
        return 'center';
      default:
        return undefined;
    }
  }};
  ${({ isPressble }) =>
    isPressble === true &&
    css`
      user-select: none;
      -ms-user-select: none;
      -moz-user-select: none;
      -ms-touch-select: none;
      -webkit-user-select: none;
      cursor: pointer;
      &:active {
        opacity: 0.9;
      }
    `}

  ${({ disableDrag }) =>
    disableDrag === true &&
    css`
      user-drag: none;
      -moz-window-dragging: none;
      -webkit-user-drag: none;
    `}
`;

export const BgContainer = styled.div`
  border-radius: 20px;
  display: flex;
  position: relative;
  margin: 4px 4px 10px 4px;
  min-height: 198px;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: #000000;
`;

// FOR IMAGE BACKGROUND

interface BgImgContainerType {
  borderRadius?: string | undefined;
  background?: string | undefined;
  minHeight?: string | undefined;
  mask?: string | undefined;
}

export const BgImageContainer = styled.div<BgImgContainerType>`
  -webkit-touch-callout: none !important;
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: ${({ minHeight }) => minHeight};
  border-radius: ${({ borderRadius }) => borderRadius};
  width: 100%;
  height: 100%;
  background: ${({ background }) => background};
`;

export const BgImage = styled.img<BgImgContainerType>`
  -webkit-touch-callout: none !important;
  alt: 'Image Masking';
  object-fit: cover;
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: ${({ borderRadius }) => borderRadius};
  z-index: -1;
  mask: ${p => p.mask};
  -webkit-mask: ${p => p.mask};
  -webkit-mask-image: ${p => p.mask};
  mask-image: ${p => p.mask};
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-user-drag: none;
`;

export interface BlockquouteProps {
  direction?: string | undefined;
  align?: 'start' | 'end' | 'center' | 'between' | 'evenly' | undefined;
  width?: string | undefined;
  height?: string | undefined;
  wrap?: string | undefined;
  margin?: string;
  padding?: string;
  position?:
    | 'fixed'
    | 'relative'
    | 'sticky'
    | 'absolute'
    | 'static'
    | '-webkit-sticky'
    | 'inherit'
    | 'initial'
    | '-ms-page'
    | 'unset'
    | undefined;
  display?: string;
  disableSelectText?: boolean;
  disableDrag?: boolean;
  isPressble?: boolean;
  zIndex?: number;
  opacity?: number;
  minHeight?: string | undefined;
  maxHeight?: string;
  minWidth?: string;
  maxWidth?: string;
  border?: string | undefined;
  borderRadius?: string | undefined;
  background?: string | undefined;
  flexGrow?: number | undefined;
  flexShrink?: number | undefined;
  flexWrap?: string | undefined;
  shadow?: string | undefined;
}

export const Blockquoute = styled.blockquote<BlockquouteProps>`
  appearance: none;
  -webkit-appearance: none;
  position: ${({ position }) => position};
  display: ${({ display }) => display ?? 'flex'};
  margin: ${({ margin }) => margin};
  padding: ${({ padding }) => padding};
  opacity: ${({ opacity }) => opacity};
  flex-wrap: ${({ wrap }) => wrap};
  width: ${({ width }) => (width ? width : '100%')};
  height: ${({ height }) => (height ? height : 'auto')};
  flex-direction: ${({ direction }) => direction};
  z-index: ${({ zIndex }) => zIndex};
  box-shadow: ${({ shadow }) => shadow};
  border: ${({ border }) => border ?? 'none'};
  border-radius: ${({ borderRadius }) => borderRadius};
  background: ${({ background }) => background};
  align-items: ${p => {
    switch (p.align) {
      case 'center':
        return 'center';
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'between':
        return 'space-between';
      case 'evenly':
        return 'space-evenly';
      default:
        return undefined;
    }
  }};
  justify-content: ${p => {
    switch (p.align) {
      case 'center':
        return 'center';
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'between':
        return 'space-between';
      case 'evenly':
        return 'space-evenly';
      default:
        return undefined;
    }
  }};
  ${p =>
    p.disableSelectText === true &&
    css`
      user-select: none;
      -ms-user-select: none;
      -moz-user-select: none;
      -ms-touch-select: none;
      -webkit-user-select: none;
    `}
  ${p =>
    p.disableDrag === true &&
    css`
      user-drag: none;
      -moz-window-dragging: none;
      -webkit-user-drag: none;
    `}
  ${({ isPressble }) =>
    isPressble === true &&
    css`
      user-select: none;
      -ms-user-select: none;
      -moz-user-select: none;
      -ms-touch-select: none;
      -webkit-user-select: none;
      cursor: pointer;
      &:active {
        opacity: 0.9;
      }
    `}
    margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 5px;
  margin-inline-end: 5px;
`;
